import { AppErrorBoundary } from '@/components/errors/AppErrorBoundary';
import { PeppolPendingInvoicesModal } from '@/components/business/PeppolPendingInvoicesModal';
import DraggableCalculatorModal from '@/components/ui/DraggableCalculatorModal';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { ROUTES } from '@/constants/routes';
import { createLayoutConfig } from '@/config/layoutConfig';
import type { NavItem } from '@/config/layoutConfig';
import { scheduleIdlePrefetch } from '@/utils/routePrefetch';
import { purchasePaymentService } from '@/services/purchasePaymentService';
import { useQuery } from '@tanstack/react-query';
import { ChatIA } from '@/components/chat/ChatIA';
import { ShieldExclamationIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { LuxModal, LuxModalBody, LuxModalHeader } from '@components/ui';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AppFooter } from './AppFooter';
import { AppHeader } from './AppHeader';
import { SavAlertModal } from './SavAlertModal';
import { AppNavbar } from './AppNavbar';
import { AppSidebar } from './AppSidebar';
import { ContentContainer } from './ContentContainer';

/**
 * Layout principal de l'application dashboard.
 *
 * Structure :
 *   AppLayout
 *   ├── Header
 *   ├── Navbar
 *   ├── Body (flex)
 *   │   ├── Sidebar
 *   │   └── ContentContainer
 *   └── Footer
 *
 * Extensible : ajouter des modules dans layoutConfig.tsx
 */
const AUDIT_SUPERVISOR_EMAILS = ['daniel.gosset@hotmail.com', 'bedia@eurodecor-meubles.eu'];

export function AppLayout() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // Écouter l'événement custom depuis la homepage (bouton rapport)
  useEffect(() => {
    const handler = () => setReportModalOpen(true);
    window.addEventListener('open-report-modal', handler);
    return () => window.removeEventListener('open-report-modal', handler);
  }, []);

  // Badge : nombre de factures à payer
  const { data: aPayerCount } = useQuery({
    queryKey: ['a-payer-count'],
    queryFn: () => purchasePaymentService.getAPayerCount(),
    staleTime: 60000,
    refetchInterval: 120000,
  });

  const layoutConfig = useMemo(() => {
    const config = createLayoutConfig(path => navigate(path));

    // Injecter le badge sur "Factures à payer" dans le menu Comptabilité
    if (aPayerCount && aPayerCount > 0) {
      const comptaMenu = config.navItems.find(
        (item): item is Extract<NavItem, { type: 'dropdown' }> =>
          item.type === 'dropdown' && item.id === 'comptabilite'
      );
      if (comptaMenu) {
        const facturesItem = comptaMenu.subItems.find(s => s.name === 'Factures à payer');
        if (facturesItem) {
          facturesItem.badge = aPayerCount;
        }
      }
    }

    // Injecter "Surveillance acomptes" dans Statistiques pour les superviseurs
    if (user?.email && AUDIT_SUPERVISOR_EMAILS.includes(user.email)) {
      const statsMenu = config.navItems.find(
        (item): item is Extract<NavItem, { type: 'dropdown' }> =>
          item.type === 'dropdown' && item.id === 'statistiques'
      );
      if (statsMenu) {
        statsMenu.subItems.push({
          name: 'Surveillance acomptes',
          icon: ShieldExclamationIcon,
          action: () => navigate(ROUTES.PAYMENT_DELETION_AUDIT),
          prefetchPath: ROUTES.PAYMENT_DELETION_AUDIT,
        });
      }
    }

    return config;
  }, [navigate, aPayerCount, user?.email]);

  useEffect(() => {
    scheduleIdlePrefetch();
  }, []);

  const handleOutsideClick = () => {
    setOpenDropdown(null);
  };

  const handleDropdownToggle = (dropdownId: string) => {
    setOpenDropdown(openDropdown === dropdownId ? null : dropdownId);
  };

  const handleSubItemClick = (action: () => void) => {
    action();
    setOpenDropdown(null);
  };

  const getMainBgClass = () => {
    if (resolvedTheme === 'light') return 'bg-gray-50';
    if (resolvedTheme === 'dark') return 'bg-slate-900';
    return 'bg-gray-50';
  };

  return (
    <div
      className={`h-screen ${getMainBgClass()} flex flex-col overflow-hidden`}
      onClick={handleOutsideClick}
    >
      <AppHeader sidebarOpen={sidebarOpen} onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

      <AppNavbar
        items={layoutConfig.navItems}
        openDropdown={openDropdown}
        onDropdownToggle={handleDropdownToggle}
        onSubItemClick={handleSubItemClick}
      />

      <div className={`flex-1 flex min-h-0 ${getMainBgClass()} overflow-hidden`}>
        <AppSidebar
          isOpen={sidebarOpen}
          items={layoutConfig.sidebarItems}
          onCalculatorClick={() => setCalculatorOpen(true)}
          onReportClick={() => setReportModalOpen(true)}
        />

        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <ContentContainer>
          <div className="flex flex-col h-full min-h-0">
            <div className="flex-1 min-h-0 overflow-hidden">
              <AppErrorBoundary>
                <Outlet />
              </AppErrorBoundary>
            </div>
          </div>
        </ContentContainer>
      </div>

      <AppFooter />
      <SavAlertModal />

      <DraggableCalculatorModal isOpen={calculatorOpen} onClose={() => setCalculatorOpen(false)} />

      <PeppolPendingInvoicesModal />

      {/* Modale Rapport du jour */}
      <LuxModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        animationState={reportModalOpen ? 'in' : null}
        size="xl"
        variant="default"
        closeOnBackdrop
        closeOnEscape
        showCloseButton
        className="!max-w-5xl"
      >
        <LuxModalHeader variant="default" showCloseButton onClose={() => setReportModalOpen(false)}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <SparklesIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink">Assistant Luxori</h3>
              <p className="text-xs text-muted mt-0.5">Interrogez vos données en langage naturel</p>
            </div>
          </div>
        </LuxModalHeader>
        <LuxModalBody className="!p-0" style={{ backgroundColor: '#fafafa', height: '60vh' }}>
          <ChatIA />
        </LuxModalBody>
      </LuxModal>
    </div>
  );
}
